import React, { useState, useEffect } from 'react';
import { MoreVertical, Edit, Trash2, LogOut, X } from 'lucide-react';

const HabitTrackerClean = ({ userId }) => {
  const [activeView, setActiveView] = useState('today');
  const [habits, setHabits] = useState([]);
  const [communityHabits, setCommunityHabits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [communitySortBy, setCommunitySortBy] = useState('popular');
  
  const [newHabit, setNewHabit] = useState({
    title: '',
    category: 'Content',
    frequency: 'daily',
    description: '',
    isPublic: false
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const recommendedHabits = [
    { title: 'Make 5 sales calls', category: 'Sales', frequency: 'daily', description: 'Reach out to prospects daily' },
    { title: 'Post 1 piece of content', category: 'Content', frequency: 'daily', description: 'Share valuable content' },
    { title: 'Engage with 10 new prospects', category: 'Outreach', frequency: 'daily', description: 'Build relationships' },
    { title: 'Read 10 minutes of industry news', category: 'Learning', frequency: 'daily', description: 'Stay informed' },
    { title: 'Send follow-up messages', category: 'Outreach', frequency: 'daily', description: 'Nurture network' },
    { title: 'Review analytics for 10 minutes', category: 'Admin', frequency: 'daily', description: 'Track performance' }
  ];

  useEffect(() => {
    loadHabits();
    if (activeView === 'community') loadCommunityHabits();
  }, [activeView, communitySortBy]);

  useEffect(() => {
    if (showDetailModal) loadDetailData(showDetailModal.id);
  }, [showDetailModal]);

  const loadHabits = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHabits(data.habits || []);
      } else {
        console.error('Failed to load habits:', response.status);
      }
    } catch (error) {
      console.error('Error loading habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCommunityHabits = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/community?sort_by=${communitySortBy}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCommunityHabits(data.habits || []);
      }
    } catch (error) {
      console.error('Error loading community habits:', error);
    }
  };

  const loadDetailData = async (habitId) => {
    try {
      const token = localStorage.getItem('access_token');
      const [membersRes, historyRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/habits/${habitId}/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/habits/${habitId}/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const members = membersRes.ok ? await membersRes.json() : { members: [] };
      const history = historyRes.ok ? await historyRes.json() : { currentStreak: 0, longestStreak: 0, completionRate: 0 };
      
      setDetailData({ members: members.members || [], history });
    } catch (error) {
      console.error('Error loading detail data:', error);
    }
  };

  const createHabit = async () => {
    if (!newHabit.title.trim()) {
      alert('Please enter a habit title');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newHabit.title,
          business_category: newHabit.category,
          frequency: newHabit.frequency,
          isGlobal: newHabit.isPublic
        })
      });
      if (response.ok) {
        await loadHabits();
        setShowModal(false);
        setNewHabit({ title: '', category: 'Content', frequency: 'daily', description: '', isPublic: false });
        alert('Habit created successfully! 🎉');
      } else {
        console.error('Failed to create habit');
      }
    } catch (error) {
      console.error('Error creating habit:', error);
    }
  };

  const completeHabit = async (habitId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/${habitId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: new Date().toISOString() })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`+${data.pointsAwarded || 5} Success Points earned! 🎯`);
        await loadHabits();
      }
    } catch (error) {
      console.error('Error completing habit:', error);
    }
  };

  const deleteHabit = async (habitId) => {
    if (!confirm('Are you sure you want to delete this habit? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/${habitId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Habit deleted successfully');
        await loadHabits();
        setShowDetailModal(null);
      }
    } catch (error) {
      console.error('Error deleting habit:', error);
    }
  };

  const leaveHabit = async (habitId) => {
    if (!confirm('Are you sure you want to leave this habit?')) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/${habitId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Left habit successfully');
        await loadHabits();
        setShowDetailModal(null);
      }
    } catch (error) {
      console.error('Error leaving habit:', error);
    }
  };

  const joinHabit = async (habitId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/habits/${habitId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('Joined habit successfully!');
        await loadHabits();
        await loadCommunityHabits();
        setActiveView('all');
      }
    } catch (error) {
      console.error('Error joining habit:', error);
    }
  };

  const useTemplate = (template) => {
    setNewHabit({
      title: template.title,
      category: template.category,
      frequency: template.frequency,
      description: template.description,
      isPublic: false
    });
    setShowModal(true);
  };

  const getTodaysHabits = () => habits.filter(h => !h.completedToday);
  
  const getStreakIcon = (streak) => {
    if (streak >= 30) return { icon: '👑', color: '#EAB308', label: 'Crown Streak!' };
    if (streak >= 10) return { icon: '🔥', color: '#F59E0B', label: 'Gold Flame!' };
    if (streak >= 3) return { icon: '🔥', color: '#F97316', label: 'On Fire!' };
    return { icon: '⚡', color: '#9CA3AF', label: 'Building...' };
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Content': { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
      'Sales': { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
      'Outreach': { bg: '#E9D5FF', text: '#6B21A8', border: '#C084FC' },
      'Learning': { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
      'Admin': { bg: '#FED7AA', text: '#9A3412', border: '#FDBA74' }
    };
    return colors[category] || colors['Learning'];
  };

  const getPoints = (freq) => ({ 'daily': 5, 'weekly': 15, 'monthly': 50 }[freq] || 5);

  const isCreator = (habit) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return habit.creatorId === user.id;
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', fontSize: '16px', color: '#6B7280' }}>Loading...</div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #E5E7EB', marginBottom: '24px', display: 'flex', gap: '8px' }}>
        {['today', 'all', 'community'].map(view => (
          <button key={view} onClick={() => setActiveView(view)} style={{
            padding: '12px 20px', background: 'none', border: 'none',
            borderBottom: activeView === view ? '3px solid #D97706' : '3px solid transparent',
            color: activeView === view ? '#D97706' : '#6B7280',
            fontWeight: '600', cursor: 'pointer', fontSize: '15px'
          }}>
            {view === 'today' ? '✓ Today\'s Actions' : view === 'all' ? '🏆 Your Habits' : '👥 Community'}
          </button>
        ))}
      </div>

      {/* Today's Actions */}
      {activeView === 'today' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div><h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 4px 0' }}>Today's Actions</h2>
            <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
              {getTodaysHabits().length === 0 ? 'All done! 🎉' : `${getTodaysHabits().length} to complete`}
            </p></div>
            <button onClick={() => setShowModal(true)} style={{ background: '#D97706', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ New</button>
          </div>
          {getTodaysHabits().length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px', background: 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)', borderRadius: '16px', border: '2px solid #6EE7B7' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
              <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 8px 0', color: '#065F46' }}>All done for today!</h3>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {getTodaysHabits().map(habit => {
                const streak = habit.userStreak || 0;
                const streakInfo = getStreakIcon(streak);
                const categoryStyle = getCategoryColor(habit.category);
                return (
                  <div key={habit.id} style={{ background: 'white', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '10px', alignItems: 'center' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>{habit.title}</h3>
                        <span style={{ background: categoryStyle.bg, color: categoryStyle.text, padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', border: `1px solid ${categoryStyle.border}` }}>{habit.category}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '20px', fontSize: '14px', color: '#6B7280' }}>
                        <span>Frequency: <strong style={{ textTransform: 'capitalize' }}>{habit.frequency}</strong></span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '18px' }}>{streakInfo.icon}</span>
                          <strong style={{ color: streakInfo.color }}>Streak: {streak} days</strong>
                        </span>
                      </div>
                    </div>
                    <button onClick={() => completeHabit(habit.id)} style={{ background: '#D97706', color: 'white', padding: '14px 32px', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '16px' }}>
                      ✓ Mark Completed
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Your Habits */}
      {activeView === 'all' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>Your Habits</h2>
            <button onClick={() => setShowModal(true)} style={{ background: '#D97706', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>+ Create</button>
          </div>
          <div style={{ marginBottom: '24px', padding: '20px', background: '#FEF3C7', borderRadius: '12px', border: '2px dashed #FCD34D' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 12px 0', color: '#92400E' }}>💡 Recommended Habits</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
              {recommendedHabits.map((t, i) => (
                <button key={i} onClick={() => useTemplate(t)} style={{ background: 'white', border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px', textAlign: 'left', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#78350F' }}>{t.title}</button>
              ))}
            </div>
          </div>
          {habits.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px', background: '#F9FAFB', borderRadius: '16px', border: '2px dashed #D1D5DB' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎯</div>
              <h3 style={{ fontSize: '22px', fontWeight: 'bold', margin: '0 0 8px 0' }}>No habits yet</h3>
              <p style={{ fontSize: '16px', color: '#6B7280', marginBottom: '24px' }}>Start building your success system</p>
              <button onClick={() => setShowModal(true)} style={{ background: '#D97706', color: 'white', padding: '14px 28px', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Create Habit</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {habits.map(habit => {
                const streak = habit.userStreak || 0;
                const streakInfo = getStreakIcon(streak);
                const categoryStyle = getCategoryColor(habit.category);
                const isDue = !habit.completedToday;
                const creator = isCreator(habit);
                return (
                  <div key={habit.id} style={{ background: 'white', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', position: 'relative' }}>
                    {/* Menu */}
                    <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                      <button onClick={(e) => { e.stopPropagation(); setShowMenuFor(showMenuFor === habit.id ? null : habit.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF' }}>
                        <MoreVertical style={{ width: '20px', height: '20px' }} />
                      </button>
                      {showMenuFor === habit.id && (
                        <div style={{ position: 'absolute', right: 0, top: '32px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '150px', zIndex: 1000 }}>
                          {creator ? (
                            <>
                              <button onClick={() => { setShowMenuFor(null); alert('Edit coming soon'); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Edit style={{ width: '16px', height: '16px' }} /> Edit
                              </button>
                              <button onClick={() => { setShowMenuFor(null); deleteHabit(habit.id); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#DC2626' }}>
                                <Trash2 style={{ width: '16px', height: '16px' }} /> Delete
                              </button>
                            </>
                          ) : (
                            <button onClick={() => { setShowMenuFor(null); leaveHabit(habit.id); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#DC2626' }}>
                              <LogOut style={{ width: '16px', height: '16px' }} /> Leave
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0', paddingRight: '30px' }}>{habit.title}</h3>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      <span style={{ background: categoryStyle.bg, color: categoryStyle.text, padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', border: `1px solid ${categoryStyle.border}` }}>{habit.category}</span>
                      {isDue ? <span style={{ background: '#FEF3C7', color: '#92400E', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>Due today</span> : <span style={{ background: '#D1FAE5', color: '#065F46', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>✓ Done</span>}
                      {habit.isPublic && <span style={{ background: '#E0E7FF', color: '#3730A3', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>👥 Public</span>}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                      <p style={{ margin: '0 0 4px 0' }}><strong>Frequency:</strong> <span style={{ textTransform: 'capitalize' }}>{habit.frequency}</span></p>
                      <p style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '18px' }}>{streakInfo.icon}</span>
                        <strong style={{ color: streakInfo.color }}>Streak: {streak} days</strong>
                      </p>
                      {habit.memberCount > 1 && <p style={{ margin: '4px 0 0 0' }}>👥 {habit.memberCount} members</p>}
                    </div>
                    <button onClick={() => setShowDetailModal(habit)} style={{ width: '100%', background: 'white', color: '#D97706', padding: '10px', border: '2px solid #D97706', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>View Details</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Community */}
      {activeView === 'community' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>Community Habits</h2>
            <select value={communitySortBy} onChange={(e) => setCommunitySortBy(e.target.value)} style={{ padding: '10px 16px', border: '2px solid #E5E7EB', borderRadius: '8px', fontWeight: '500', cursor: 'pointer', background: 'white' }}>
              <option value="popular">Most Popular</option>
              <option value="newest">Newest</option>
            </select>
          </div>
          {communityHabits.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px', background: '#F9FAFB', borderRadius: '16px', border: '2px dashed #D1D5DB' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>👥</div>
              <p style={{ fontSize: '16px', color: '#6B7280' }}>No community habits yet</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {communityHabits.map(habit => {
                const categoryStyle = getCategoryColor(habit.category);
                return (
                  <div key={habit.id} style={{ background: 'white', border: '2px solid #E5E7EB', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 12px 0' }}>{habit.title}</h3>
                    <span style={{ background: categoryStyle.bg, color: categoryStyle.text, padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', marginBottom: '16px', display: 'inline-block' }}>{habit.category}</span>
                    <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
                      <p style={{ margin: '0 0 6px 0' }}><strong>Frequency:</strong> <span style={{ textTransform: 'capitalize' }}>{habit.frequency}</span></p>
                      <p style={{ margin: 0 }}><strong>👥 Members:</strong> {habit.memberCount || 0}</p>
                    </div>
                    <button onClick={() => joinHabit(habit.id)} disabled={habit.isMember} style={{ width: '100%', background: habit.isMember ? '#F3F4F6' : '#D97706', color: habit.isMember ? '#6B7280' : 'white', padding: '12px', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: habit.isMember ? 'not-allowed' : 'pointer' }}>
                      {habit.isMember ? 'Joined ✓' : 'Join Habit'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div onClick={() => setShowDetailModal(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>{showDetailModal.title}</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ background: getCategoryColor(showDetailModal.category).bg, color: getCategoryColor(showDetailModal.category).text, padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' }}>{showDetailModal.category}</span>
                  <span style={{ fontSize: '14px', color: '#6B7280', padding: '4px 12px', textTransform: 'capitalize' }}>{showDetailModal.frequency}</span>
                </div>
              </div>
              <button onClick={() => setShowDetailModal(null)} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#9CA3AF' }}>×</button>
            </div>
            
            {/* Progress */}
            <div style={{ marginBottom: '24px', padding: '20px', background: '#F9FAFB', borderRadius: '12px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0' }}>📊 Progress</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6B7280' }}>Current Streak</p>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: getStreakIcon(showDetailModal.userStreak || 0).color }}>
                    {getStreakIcon(showDetailModal.userStreak || 0).icon} {showDetailModal.userStreak || 0}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6B7280' }}>Longest Streak</p>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#F59E0B' }}>
                    {detailData?.history?.longestStreak || 0}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6B7280' }}>This Month</p>
                  <p style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#10B981' }}>
                    {detailData?.history?.completionRate || 0}%
                  </p>
                </div>
              </div>
            </div>

            {/* Members */}
            {detailData?.members && detailData.members.length > 0 && (
              <div style={{ marginBottom: '24px', padding: '20px', background: '#F9FAFB', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 16px 0' }}>👥 Members ({detailData.members.length})</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {detailData.members.map(member => (
                    <div key={member.userId} title={`${member.name} - ${member.currentStreak} day streak`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: member.avatar ? `url(${member.avatar})` : '#D97706', backgroundSize: 'cover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                        {!member.avatar && member.name.charAt(0)}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#111827' }}>{member.name}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: '#6B7280' }}>{member.currentStreak} days</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {!showDetailModal.completedToday && (
                <button onClick={() => { completeHabit(showDetailModal.id); setShowDetailModal(null); }} style={{ width: '100%', background: '#D97706', color: 'white', padding: '16px', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '16px' }}>
                  ✓ Mark Completed
                </button>
              )}
              {!isCreator(showDetailModal) && (
                <button onClick={() => leaveHabit(showDetailModal.id)} style={{ width: '100%', background: 'white', color: '#DC2626', padding: '12px', border: '2px solid #DC2626', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                  Leave Habit
                </button>
              )}
              {isCreator(showDetailModal) && (
                <button onClick={() => deleteHabit(showDetailModal.id)} style={{ width: '100%', background: 'white', color: '#DC2626', padding: '12px', border: '2px solid #DC2626', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                  Delete Habit
                </button>
              )}
              <button onClick={() => setShowDetailModal(null)} style={{ width: '100%', background: 'white', color: '#6B7280', padding: '12px', border: '2px solid #E5E7EB', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div onClick={() => setShowModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '24px' }}>Create Habit</h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#111827' }}>Habit Title *</label>
              <input type="text" value={newHabit.title} onChange={(e) => setNewHabit({ ...newHabit, title: e.target.value })} placeholder="e.g., Post 1 piece of content" style={{ width: '100%', padding: '12px', border: '2px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#111827' }}>Category *</label>
              <select value={newHabit.category} onChange={(e) => setNewHabit({ ...newHabit, category: e.target.value })} style={{ width: '100%', padding: '12px', border: '2px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', background: 'white', cursor: 'pointer' }}>
                {['Content', 'Sales', 'Outreach', 'Learning', 'Admin'].map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#111827' }}>Frequency *</label>
              <select value={newHabit.frequency} onChange={(e) => setNewHabit({ ...newHabit, frequency: e.target.value })} style={{ width: '100%', padding: '12px', border: '2px solid #D1D5DB', borderRadius: '8px', fontSize: '15px', background: 'white', cursor: 'pointer' }}>
                <option value="daily">Daily (+5 points)</option>
                <option value="weekly">Weekly (+15 points)</option>
                <option value="monthly">Monthly (+50 points)</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#111827' }}>Description (Optional)</label>
              <textarea value={newHabit.description} onChange={(e) => setNewHabit({ ...newHabit, description: e.target.value })} placeholder="Explain what this habit is or why it matters..." rows={3} style={{ width: '100%', padding: '12px', border: '2px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '24px', padding: '16px', background: '#EFF6FF', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={newHabit.isPublic} onChange={(e) => setNewHabit({ ...newHabit, isPublic: e.target.checked })} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <div>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1E3A8A' }}>Allow other users to join this habit</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#3B82F6' }}>Make available to community</p>
                </div>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, background: 'white', color: '#6B7280', padding: '14px', border: '2px solid #E5E7EB', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createHabit} style={{ flex: 1, background: '#D97706', color: 'white', padding: '14px', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HabitTrackerClean;
